import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { useLang } from "../i18n";
import { toast } from "sonner";
import StarRating from "../components/StarRating";
import Heatmap from "../components/Heatmap";
import { regionName, flagEmoji } from "../lib/countries";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip,
} from "recharts";
import { Download, FileSpreadsheet, Share2, Disc3, CalendarDays, Users, Skull, X, Copy, Check, Trash2 } from "lucide-react";

const GOLD = "#D4AF37";
const PIE_COLORS = ["#D4AF37", "#7F1D1D"];
const TOOLTIP_STYLE = { background: "#18181b", border: "1px solid #27272a", color: "#fff" };
const CURSOR_FILL = { fill: "#27272a" };

export default function Stats() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [heatYear, setHeatYear] = useState(new Date().getFullYear());
  const [concertFilter, setConcertFilter] = useState("attended");
  const [seenModal, setSeenModal] = useState(null); // { name, flag, events: [] }

  const openSeenEvents = async (b) => {
    try {
      const { data } = await api.get(`/bands/${b.id}`);
      const events = (data.concerts || [])
        .filter((c) => c.attended !== false)
        .map((c) => ({ id: c.id, event_id: c.event_id, name: c.event?.name || "—", date: c.date || c.event?.start_date || "", rating: c.rating || 0 }))
        .sort((a, z) => (z.date || "").localeCompare(a.date || ""));
      setSeenModal({ name: b.name, flag: b.flag, events });
    } catch (err) { toast.error(err.message); }
  };

  useEffect(() => {
    api.get("/share").then(({ data }) => setShareToken(data.token));
  }, []);
  useEffect(() => {
    api.get(`/stats?concert_filter=${concertFilter}`).then(({ data }) => setData(data));
  }, [concertFilter]);

  const heatCounts = useMemo(() => {
    const m = {};
    (data?.concerts_by_date || []).forEach((d) => { m[d.date] = d.value; });
    return m;
  }, [data]);
  const heatYears = useMemo(() => {
    const ys = new Set((data?.concerts_by_year || []).map((d) => Number(d.year)));
    ys.add(new Date().getFullYear());
    return [...ys].sort((a, b) => b - a);
  }, [data]);

  const doExport = async () => {
    const { data } = await api.get("/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metalvault-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("export_done"));
  };

  const doExportExcel = async () => {
    const res = await api.get("/export/excel", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metalvault-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("export_done"));
  };

  const generate = async () => {
    const { data } = await api.post("/share");
    setShareToken(data.token);
  };
  const revoke = async () => {
    await api.delete("/share");
    setShareToken(null);
  };
  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : "";
  const copy = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  if (!data) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const concertKpiLabel = concertFilter === "missed" ? t("concerts_missed_label")
    : concertFilter === "all" ? t("concerts_all_label") : t("total_concerts");
  const kpis = [
    { label: t("total_bands"), value: data.total_bands, icon: Skull },
    { label: t("total_events"), value: data.total_events, icon: CalendarDays },
    { label: concertKpiLabel, value: data.total_concerts, icon: Disc3 },
  ];
  const empty = data.total_bands === 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h1 className="heading text-4xl font-bold text-white">{t("stats")}</h1>
          <div className="flex gap-2">
            <button data-testid="export-btn" onClick={doExport} className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-200 label-caps text-sm rounded-sm hover:border-primary hover:text-primary">
              <Download size={16} /> JSON
            </button>
            <button data-testid="export-excel-btn" onClick={doExportExcel} className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-200 label-caps text-sm rounded-sm hover:border-primary hover:text-primary">
              <FileSpreadsheet size={16} /> {t("export_excel")}
            </button>
            <button data-testid="share-btn" onClick={() => setShowShare(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground label-caps text-sm rounded-sm font-semibold hover:bg-yellow-400">
              <Share2 size={16} /> {t("share")}
            </button>
          </div>
        </div>

        {empty ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Skull size={56} className="text-zinc-700 mb-4" />
            <p className="text-zinc-400">{t("no_stats")}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {kpis.map((k) => (
                <div key={k.label} data-testid={`kpi-${k.label}`} className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
                  <k.icon className="text-primary mb-2" size={22} />
                  <p className="heading text-4xl font-bold text-white">{k.value}</p>
                  <p className="label-caps text-xs text-zinc-500 mt-1">{k.label}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              <span className="label-caps text-xs text-zinc-500 mr-1">{t("concerts_filter")}</span>
              {[
                { key: "attended", label: `${t("filter_attended")} (${data.total_attended})` },
                { key: "missed", label: `${t("filter_missed")} (${data.total_missed})` },
                { key: "all", label: `${t("filter_all")} (${data.total_attended + data.total_missed})` },
              ].map((opt) => (
                <button key={opt.key} data-testid={`concert-filter-${opt.key}`} onClick={() => setConcertFilter(opt.key)}
                  className={`label-caps text-xs px-3 py-1.5 rounded-sm border transition-colors ${concertFilter === opt.key ? "border-primary text-primary bg-primary/10" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Panel title={t("heatmap")} className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-zinc-500">{t("heatmap_year")}</span>
                  <select data-testid="heatmap-year-select" value={heatYear} onChange={(e) => setHeatYear(Number(e.target.value))}
                    className="bg-black border border-zinc-800 rounded-sm px-2 py-1 text-sm text-zinc-200 focus:border-primary focus:outline-none">
                    {heatYears.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <Heatmap counts={heatCounts} year={heatYear} lang={lang} />
                <div className="flex items-center gap-2 mt-3 text-[10px] text-zinc-500">
                  <span>{t("heatmap_less")}</span>
                  <span className="w-3 h-3 rounded-[2px] bg-zinc-800/60" />
                  <span className="w-3 h-3 rounded-[2px] bg-primary/30" />
                  <span className="w-3 h-3 rounded-[2px] bg-primary/60" />
                  <span className="w-3 h-3 rounded-[2px] bg-primary" />
                  <span>{t("heatmap_more")}</span>
                </div>
              </Panel>

              <Panel title={t("by_genre")}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.by_genre} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={130} tick={{ fill: "#a1a1aa", fontSize: 12 }} />
                    <Tooltip cursor={CURSOR_FILL} contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="value" fill={GOLD} radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>

              <Panel title={t("by_category")}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={data.by_category} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                      {data.by_category.map((e, i) => <Cell key={e.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-2">
                  {data.by_category.map((e, i) => (
                    <span key={e.name} className="flex items-center gap-2 text-sm text-zinc-300">
                      <span className="w-3 h-3 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {e.name} ({e.value})
                    </span>
                  ))}
                </div>
              </Panel>

              <Panel title={t("concerts_by_year")}>
                {data.concerts_by_year.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.concerts_by_year}>
                      <XAxis dataKey="year" tick={{ fill: "#a1a1aa", fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fill: "#a1a1aa", fontSize: 12 }} />
                      <Tooltip cursor={CURSOR_FILL} contentStyle={TOOLTIP_STYLE} />
                      <Bar dataKey="value" fill={GOLD} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-zinc-500 text-sm py-10 text-center">—</p>}
              </Panel>

              <Panel title={t("top_rated")}>
                <div className="space-y-2">
                  {data.top_bands.map((b) => (
                    <button key={b.id} data-testid="top-band" onClick={() => navigate(`/bands/${b.id}`)}
                      className="w-full flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2 hover:border-zinc-600">
                      <span className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-sm overflow-hidden bg-zinc-900 flex items-center justify-center">
                          {b.logo_url ? <img src={b.logo_url} alt="" className="w-full h-full object-cover" /> : <Skull size={16} className="text-zinc-700" />}
                        </span>
                        <span className="text-white text-sm">{b.flag} {b.name}</span>
                      </span>
                      <StarRating value={b.rating} size={14} testid={`top-rating-${b.id}`} />
                    </button>
                  ))}
                </div>
              </Panel>

              <Panel title={t("top_seen")}>
                {data.top_seen_bands?.length ? (
                  <div className="space-y-2">
                    {data.top_seen_bands.map((b, i) => (
                      <button key={b.id} data-testid="top-seen-band" onClick={() => openSeenEvents(b)}
                        className="w-full flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2 hover:border-zinc-600">
                        <span className="flex items-center gap-3">
                          <span className="label-caps text-xs text-zinc-500 w-5 text-right">{i + 1}</span>
                          <span className="w-9 h-9 rounded-sm overflow-hidden bg-zinc-900 flex items-center justify-center">
                            {b.logo_url ? <img src={b.logo_url} alt="" className="w-full h-full object-cover" /> : <Skull size={16} className="text-zinc-700" />}
                          </span>
                          <span className="text-white text-sm">{b.flag} {b.name}</span>
                        </span>
                        <span className="flex items-center gap-1.5 text-primary font-semibold" data-testid={`seen-count-${b.id}`}>
                          <Disc3 size={14} /> {b.count}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : <p className="text-zinc-500 text-sm py-10 text-center">{t("no_seen_yet")}</p>}
              </Panel>

              {data.by_country.length > 0 && (
                <Panel title={t("by_country")} className="lg:col-span-2">
                  <div className="flex flex-wrap gap-3">
                    {data.by_country.map((c) => {
                      const label = c.code ? regionName(c.code, lang) : c.name;
                      const flag = c.code ? flagEmoji(c.code) : c.flag;
                      return (
                        <div key={c.code || c.name} className="flex items-center gap-2 bg-black border border-zinc-800 rounded-sm px-3 py-2">
                          <span className="text-xl">{flag}</span>
                          <span className="text-sm text-zinc-200">{label}</span>
                          <span className="text-primary font-semibold">{c.value}</span>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              )}
            </div>
          </>
        )}
      </main>

      {showShare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowShare(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="heading text-2xl text-white">{t("share_title")}</h3>
              <button onClick={() => setShowShare(false)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-sm text-zinc-400 mb-4">{t("share_desc")}</p>
            {shareToken ? (
              <>
                <div className="flex gap-2 mb-3">
                  <input data-testid="share-url" readOnly value={shareUrl} className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-zinc-200 text-sm" />
                  <button data-testid="copy-share-btn" onClick={copy} className="px-3 border border-zinc-700 rounded-sm text-primary hover:bg-zinc-800">
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                </div>
                <button data-testid="revoke-share-btn" onClick={revoke} className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300">
                  <Trash2 size={14} /> {t("revoke")}
                </button>
              </>
            ) : (
              <button data-testid="generate-share-btn" onClick={generate} className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400">
                {t("generate_link")}
              </button>
            )}
          </div>
        </div>
      )}

      {seenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSeenModal(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="heading text-2xl text-white">{seenModal.flag} {seenModal.name}</h3>
              <button onClick={() => setSeenModal(null)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            <p className="label-caps text-xs text-zinc-500 mb-4">{seenModal.events.length} {t("seen_at_events")}</p>
            <div className="space-y-2">
              {seenModal.events.map((ev) => (
                <button key={ev.id} data-testid="seen-event-row" onClick={() => navigate(`/events/${ev.event_id}`)}
                  className="w-full flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2.5 hover:border-zinc-600 text-left">
                  <span className="flex items-center gap-3">
                    <CalendarDays size={16} className="text-primary" />
                    <span>
                      <span className="block text-white text-sm">{ev.name}</span>
                      <span className="block text-zinc-500 text-xs">{ev.date}</span>
                    </span>
                  </span>
                  <StarRating value={ev.rating} size={13} testid={`seen-event-rating-${ev.id}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`bg-zinc-900 border border-zinc-800 rounded-sm p-5 min-w-0 ${className}`}>
      <h2 className="label-caps text-sm text-zinc-400 mb-4">{title}</h2>
      {children}
    </div>
  );
}
